/**
 * Azure Document Intelligence Service
 * Handles PDF/document analysis using Azure Document Intelligence API via backend
 */

import { reconstructDocumentContent } from '../utils/documentUtils';
import api from './api';

/**
 * Analyzes a PDF document using Azure Document Intelligence (via secure backend)
 * @param {File} file - PDF file to analyze
 * @param {string} extractionMethod - Extraction method: 'Neurix Layout', 'Neurix Layout 2.0' or 'Mistral 2505'
 * @param {boolean} applyZoom - Whether to zoom PDF pages before extraction to improve OCR/text quality
 * @param {boolean} applyHighResOcr - Whether to request Azure DI's ocrHighResolution add-on for small/dense text
 * @returns {Promise<Object>} - Extracted text content
 */
export async function analyzeDocument(file, extractionMethod = 'Neurix Layout', applyZoom = false, applyHighResOcr = false) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('extractionMethod', extractionMethod);
  formData.append('applyZoom', applyZoom ? 'true' : 'false');
  formData.append('applyHighResOcr', applyHighResOcr ? 'true' : 'false');

  console.log(`[INFO] Sending document to backend for analysis with method: ${extractionMethod}...`);

  const response = await api.post('/api/analyze-document', formData);
  const analyzeResult = response.data;
  const serverTimingMs = analyzeResult.server_timing_ms ?? null;
  
  console.log('[INFO] Document analysis completed');

  // For Excel files (.xlsx, .xls), return raw content directly from backend
  // Backend already formats Excel text properly with pipe delimiters
  const isExcelFile = file.name && file.name.toLowerCase().match(/\.(xlsx|xls|xlsm)$/);
  
  if (isExcelFile && analyzeResult.content) {
    console.log('[INFO] Excel file detected - returning raw content directly from backend');
    return { content: analyzeResult.content, serverTimingMs };
  }

  // For Mistral 2505, return content directly
  if (extractionMethod === 'Mistral 2505' && analyzeResult.content) {
    console.log('[INFO] Mistral 2505 extraction - returning extracted text content');
    return { ...analyzeResult, content: analyzeResult.content, serverTimingMs };
  }

  if (analyzeResult.source === 'pdf-native' && analyzeResult.content) {
    console.log('[INFO] Native PDF text-layer extraction - returning backend content');
    return { ...analyzeResult, content: analyzeResult.content, serverTimingMs };
  }

  // For Lite Parse, return the backend content string directly
  if ((analyzeResult.source === 'liteparse' || analyzeResult.route_decision === 'liteparse' || extractionMethod === 'Lite Parse') && analyzeResult.content) {
    console.log('[INFO] Lite Parse extraction - returning backend content');
    return { ...analyzeResult, content: analyzeResult.content, serverTimingMs };
  }

  if (analyzeResult.source === 'azure-di-layout-markdown' && analyzeResult.content) {
    console.log('[INFO] Azure DI prebuilt-layout markdown extraction - returning backend content');
    return { ...analyzeResult, content: analyzeResult.content, serverTimingMs };
  }

  // For non-Excel files (PDF, images) with Azure DI, use backend reconstruction if available, else client-side
  console.log('[INFO] Non-Excel file - using reconstructed content');
  const content = reconstructDocumentContent(analyzeResult);
  return { ...analyzeResult, content, serverTimingMs };
}
